import { Query } from '@cubejs-client/core';
import { useMemo } from 'react';
import * as Yup from 'yup';
import { useCubeQueryCustom } from '../../../services/data/customerSummary/hooks/useCubeQueryCustom';
import {
  CubeDimensionEnum,
  CubeMeasureEnum,
} from '../../../services/data/impl/cube/cubeSlice';
import { updateQueryParams } from '../../../services/data/impl/cube/cubeUtils';
import { CubePaginationOptions } from '../../../services/data/impl/cube/hooks/useCubeQueryChunkLoadingSync';

// This query runs when a customer is selected in the customer dropdown.
// It fetches all the AI detection data for the selected customer.
// This is distributed throughout the app through the customer summary context and has two main uses:
// 1. Builds a dynamic data registry of AI services run for each scan that populates the feature dropdowns and determines the correct service versions to use.
// 2. Fetches the ranch summary table data.
const customerSummarySchema = Yup.object().shape({
  [CubeMeasureEnum.detectionFactAvgCountPerMeter]: Yup.number().required(),
  [CubeMeasureEnum.DetectionFactAvgLength]: Yup.number().nullable(),
  [CubeMeasureEnum.DetectionFactHasColorHSV]: Yup.boolean().nullable(),
  [CubeDimensionEnum.detectionFactRanchId]: Yup.string().required(),
  [CubeDimensionEnum.ranchName]: Yup.string().required(),
  [CubeDimensionEnum.detectionFactBlockId]: Yup.string().required(),
  [CubeDimensionEnum.blockName]: Yup.string().required(),
  [CubeDimensionEnum.detectionFactScanId]: Yup.string().required(),
  [CubeDimensionEnum.scanDate]: Yup.string().required(),
  [CubeDimensionEnum.className]: Yup.string().required(),
  [CubeDimensionEnum.modelName]: Yup.string().required(),
  [CubeDimensionEnum.modelVersion]: Yup.string().required(),
  [CubeMeasureEnum.detectionFactEstimatedCount]: Yup.number().nullable(),
  [CubeMeasureEnum.detectionFactTraveledDistance]: Yup.number().nullable(),
});

export type CustomerSummary = Yup.InferType<typeof customerSummarySchema>;

const customerSummaryQuery: Query = {
  measures: [
    CubeMeasureEnum.detectionFactAvgCountPerMeter,
    CubeMeasureEnum.DetectionFactAvgLength,
    CubeMeasureEnum.DetectionFactHasColorHSV,
    CubeMeasureEnum.detectionFactEstimatedCount,
    CubeMeasureEnum.detectionFactTraveledDistance,
  ],
  dimensions: [
    CubeDimensionEnum.detectionFactRanchId,
    CubeDimensionEnum.ranchName,
    CubeDimensionEnum.detectionFactBlockId,
    CubeDimensionEnum.blockName,
    CubeDimensionEnum.detectionFactScanId,
    CubeDimensionEnum.scanDate,
    CubeDimensionEnum.className,
    CubeDimensionEnum.modelName,
    CubeDimensionEnum.modelVersion,
  ],
  order: [[CubeDimensionEnum.scanDate, 'desc']],
};

/**
 *
 * Query and validate customer summary data from Cube
 *
 * @template T - The interface/type of the individual data item, extending Yup.AnyObject.
 * @param {boolean} skip - Whether to skip the query execution or not.
 * @param {string} currScanId - The current scan ID for updating the query parameters.
 *  @param {string} currPointId - The current point ID for updating the query parameters.
 *  @param {string} parseJSONString - A boolean to indicate whether to parse the raw data from a JSON string or not.
 *  @param {string} customerId - The current customer ID for updating the query parameters.
 * @param {CubePaginationOptions} [paginationOptions] - Optional pagination options for the query.
 * @param {number | null} [daysSinceNowServerSideScanDateFilter] - Optional number of days since the current date to filter the data by server side.
 * @returns {{
 *  rawData: any[],
 *  validatedData: T[] | null,
 *  isLoading: boolean,
 *  progress: number,
 *  error: any,
 * }} - An object containing the raw data, validated data, loading status, progress, and error.
 */
export function useCustomerSummaryQuery(
  skip: boolean,
  currScanId?: string,
  currPointId?: string,
  parseJSONString = false,
  customerId?: string,
  paginationOptions?: CubePaginationOptions,
  daysSinceNowServerSideScanDateFilter?: number | null
) {
  // Update the query with the current IDs / parameters
  const updatedQuery = useMemo(() => {
    const newQuery: Query = updateQueryParams(
      customerSummaryQuery,
      currScanId,
      currPointId,
      customerId,
      daysSinceNowServerSideScanDateFilter
    );
    return newQuery;
  }, [
    currScanId,
    currPointId,
    customerId,
    daysSinceNowServerSideScanDateFilter,
  ]);

  return useCubeQueryCustom(
    updatedQuery,
    skip,
    customerSummarySchema,
    parseJSONString,
    paginationOptions
  );
}
